import React from "react";
import moment from "moment";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { ChevronDown } from "@sonder/ui/source/icons";
import { Avatar } from "@sonder/ui/source/styled/components/Avatar";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { DropdownContent } from "@sonder/ui/source/styled/components/DropdownContent";
import { ArchivedComment } from "@sonder/ui/source/styled/fragments/ArchivedComment";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  CommentAuthor,
  CommentContent,
  CommentCreatedAt,
  Comment as CommentFragment,
  CommentGutter,
  CommentHeader,
} from "@sonder/ui/source/styled/fragments/Comment";
import { PlainButton } from "@sonder/ui/source/styled/fragments/PlainButton";

export const getCreatedAtDescriptionFromDate = (date: string) =>
  moment(date).calendar(moment(), {
    sameDay: "[Today at] LT",
    nextDay: "[Tomorrow at] LT",
    nextWeek: "ll [at] LT",
    lastDay: "[Yesterday at] LT",
    lastWeek: "ll [at] LT",
    sameElse: "ll [at] LT",
  });

interface CommentProps {
  loggedInUserId: string;
  authorName: string;
  authorId: string;
  avatarUrl?: string;
  archived?: boolean;
  createdAt: string;
  children: React.ReactNode;
  onArchive?(): void;
  onUndoArchive?(): void;
}
export const Comment = ({
  loggedInUserId,
  authorName,
  authorId,
  avatarUrl,
  archived = false,
  createdAt,
  children,
  onArchive = () => undefined,
  onUndoArchive,
}: CommentProps) => {
  const isOwnComment = loggedInUserId === authorId;
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  if (archived && !isOwnComment) return null;
  if (archived && isOwnComment) {
    return <ArchivedComment onUndoArchive={onUndoArchive} />;
  }

  return (
    <CommentFragment onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <CommentGutter>
        <Avatar imageUrl={avatarUrl} name={authorName} />
      </CommentGutter>
      <CommentContent>
        <CommentHeader>
          <CommentAuthor>{isOwnComment ? "You" : authorName}</CommentAuthor>
          <CommentCreatedAt>
            {getCreatedAtDescriptionFromDate(createdAt)}
          </CommentCreatedAt>
          {hovered && isOwnComment && (
            <Dropdown
              trigger={
                <PlainButton>
                  <ChevronDown />
                </PlainButton>
              }
            >
              {({ isOpen, closePortal }) => {
                return (
                  isOpen && (
                    <DropdownContent align="right">
                      <Button
                        onClick={() => {
                          if (!archived) onArchive();
                          closePortal();
                        }}
                      >
                        Delete
                      </Button>
                    </DropdownContent>
                  )
                );
              }}
            </Dropdown>
          )}
        </CommentHeader>
        {children}
      </CommentContent>
    </CommentFragment>
  );
};
